import { useCallback, useMemo } from "react";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { formattingUtils } from "@flights/web-api-utils-universal";
import makeTimelineFromSegment from "../../../../../store/selectors/timelineFromSegment.js";
import useSearchCabinClass from "../../../../../hooks/useSearchCabinClass.js";
import { SPACER_PADDED } from "@flights/constants";
import { isVirtualInterliningFlight } from "../../../../../hooks/useVirutalInterlining.js";
export function useFlightDetailsTimelineController(props) {
    const { flight } = props;
    const i18n = useI18n();
    const { searchCabinClass } = useSearchCabinClass();
    const title = useMemo(() => "Your Flights", []);
    const durationText = useCallback((s) => {
        return formattingUtils.flightDuration(s.durationMinutes, i18n);
    }, [i18n]);
    const durationTextAria = useCallback((s) => {
        return formattingUtils.flightDurationAria(s.durationMinutes, i18n);
    }, [i18n]);
    const stopsText = useCallback((s) => {
        return s.stops === 0
            ? i18n.trans(t("flights_filter_stops_none"))
            : i18n.trans(t("flights_route_num_stops", { num_exception: s.stops, variables: { num_stops: s.stops } }));
    }, [i18n]);
    const isMismatchCabinClass = useCallback((s) => {
        const isMismatch = s.legs.some((l) => l.cabinClass !== searchCabinClass);
        return isMismatch ? i18n.trans(t("flights_fd_cabin_mismatch")) : "";
    }, [searchCabinClass, i18n]);
    const cabinClassText = useCallback((s) => {
        if (isMismatchCabinClass(s))
            return i18n.trans(t("flights_fd_cabin_mismatch"));
        const legCabinClass = s.legs[0].cabinClass;
        const isSimilar = s.legs.every((l) => l.cabinClass === legCabinClass);
        if (!isSimilar)
            return "";
        switch (legCabinClass) {
            case "ECONOMY":
                return i18n.trans(t("flights_cabin_class_economy_option"));
            case "PREMIUM_ECONOMY":
                return i18n.trans(t("flights_cabin_class_premium_option"));
            case "BUSINESS":
                return i18n.trans(t("flights_cabin_class_business_option"));
            case "PREMIUM_BUSINESS":
                return i18n.trans(t("flights_cabin_class_premium_business"));
            case "FIRST":
                return i18n.trans(t("flights_cabin_class_first_option"));
            case "PREMIUM_FIRST":
                return i18n.trans(t("flights_cabin_class_premium_first"));
            default:
                return "";
        }
    }, [i18n, isMismatchCabinClass]);
    const titleText = useCallback((s) => {
        return i18n.trans(t("flights_details_route", {
            variables: {
                city_name: s.arrivalAirport.cityName || s.arrivalAirport.name
            }
        }));
    }, [i18n]);
    const titleInlineText = useCallback((s) => {
        return i18n.trans(t("flights_fd_route", {
            variables: {
                origin_city: s.departureAirport.cityName || s.departureAirport.name,
                origin_iata: s.departureAirport.city || s.departureAirport.code,
                destination_city: s.arrivalAirport.cityName || s.arrivalAirport.name,
                destination_iata: s.arrivalAirport.city || s.arrivalAirport.code
            }
        }));
    }, [i18n]);
    const subtitleText = useCallback((s) => {
        return [stopsText(s), durationText(s), cabinClassText(s)].filter(Boolean).join(SPACER_PADDED);
    }, [stopsText, durationText, cabinClassText]);
    const subtitleAriaText = useCallback((s) => {
        return [stopsText(s), durationTextAria(s), cabinClassText(s)].filter(Boolean).join(" - ");
    }, [stopsText, durationTextAria, cabinClassText]);
    const codes = useCallback((s) => {
        return [...new Set(s.timeline?.filter((t) => t.type === "FLIGHT")?.map((t) => t.carrierCode))]
            .filter(Boolean)
            .flat();
    }, []);
    const segments = useMemo(() => flight.segments.map((s, idx) => {
        const segment = makeTimelineFromSegment(s, idx);
        const timeline = segment.timeline;
        const title = titleText(segment);
        const titleInline = titleInlineText(segment);
        const airlineCodes = codes(segment);
        const subtitle = subtitleText(segment);
        const subtitleAria = subtitleAriaText(segment);
        const isVirtualInterlining = isVirtualInterliningFlight(segment);
        return {
            title,
            titleInline,
            subtitle,
            subtitleAria,
            airlineCodes,
            timeline,
            data: segment,
            isVirtualInterlining
        };
    }), [flight, titleText, titleInlineText, subtitleText, subtitleAriaText, codes]);
    const areAllSegmentsDirect = useMemo(() => flight.segments.every((s) => s.legs.length === 1), [flight.segments]);
    return {
        title,
        segments,
        areAllSegmentsDirect
    };
}
